import { useEffect, useMemo, useRef } from "react";
import GraphChart from "./GraphChart";
import GraphMenu from "./GraphMenu";
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { parseGraph } from "../../utils/parsers";
import { useApi } from "../../contexts/ApiProvider";
import { useSurveyMode } from "../../contexts/SurveyModeProvider";
import { patientActions } from "../../actions";

function Graph(micro_id) {
  const api = useApi();
  const dispatch = useDispatch();

  const ptId = useSelector((state) => state.patient.micro_id);
  const graph = useSelector((state) => state.patient.graph);
  const adm_start = useSelector((state) => state.patient.patient.pt.adm_start);
  const nodes = useMemo(() => graph.nodes || [], [graph.nodes]);
  const edges = useMemo(() => graph.edges || [], [graph.edges]);
  const treeNameMap = useSelector(
    (state) => state.patient.tree.name_map || {}
  );
  const treeNameMapSnp2 = useSelector(
    (state) => state.patient.tree.name_map_snp2 || {}
  );
  const survey = useSelector((state) => state.survey);
  const activeCase = useSelector((state) => state.survey.activeCase);
  const debugSurveyMode = useSelector(
    (state) => state.survey.debug_survey_mode
  );
  const { debug_mode } = useSurveyMode();
  const pruneForPhylos = useSelector(
    (state) => state.patient.graph.pruneForPhylos
  );
  const graphConstraints = useSelector(
    (state) => state.patient.graph.constrain || []
  );
  const overlap_constraint = useSelector(
    (state) => state.patient.tree.overlap_constraint
  );
  const treeConstraintSeqIds = useSelector(
    (state) => state.patient.tree.constrain || []
  );
  const graphLoading = useSelector((state) => state.patient.graph.loading);
  const graphProgress = useSelector((state) => state.patient.graph.progress);

  const layers = useSelector((state) => state.patient.layers);
  const incubation = useSelector((state) => state.patient.patient.incubation);
  const inf_period_before = useSelector(
    (state) => state.patient.patient.inf_period_prior
  );
  const inf_period_after = useSelector(
    (state) => state.patient.patient.inf_period_after
  );
  const contam_period = useSelector(
    (state) => state.patient.patient.contam_period
  );
  const test_time_buffer = useSelector(
    (state) => state.patient.patient.test_time_buffer
  );
  const overlap_level = useSelector(
    (state) => state.patient.patient.overlap_level
  );

  const desiredGraphKey = useMemo(() => {
    return [
      micro_id.micro_id,
      layers,
      incubation,
      inf_period_before,
      inf_period_after,
      contam_period,
      test_time_buffer,
      overlap_level,
    ].join("|");
  }, [
    micro_id.micro_id,
    layers,
    incubation,
    inf_period_before,
    inf_period_after,
    contam_period,
    test_time_buffer,
    overlap_level,
  ]);

  const graphIsFresh = graph.key === desiredGraphKey;

  const graphRequestIdRef = useRef(0);
  const graphProgressIntervalRef = useRef(null);

  const clearGraphProgressTimer = () => {
    if (graphProgressIntervalRef.current) {
      clearInterval(graphProgressIntervalRef.current);
      graphProgressIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      graphRequestIdRef.current += 1;
      clearGraphProgressTimer();
      dispatch(patientActions.graphLoadingEnd());
    };
  }, [dispatch]);

  useEffect(() => {
    if (ptId !== micro_id.micro_id) return;
    if (adm_start === null) return;
    if (graphIsFresh) return;

    const requestId = ++graphRequestIdRef.current;
    clearGraphProgressTimer();

    const jobId = `graph-${micro_id.micro_id}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    dispatch(patientActions.graphLoadingStart());
    dispatch(patientActions.graphProgressSet(0));

    graphProgressIntervalRef.current = setInterval(async () => {
      try {
        const resp = await api.get(`/graph_progress/${jobId}`);
        if (graphRequestIdRef.current !== requestId) return;
        if (resp.ok && resp.body && typeof resp.body.progress === "number") {
          dispatch(patientActions.graphProgressSet(resp.body.progress));
          if (resp.body.progress >= 100) {
            clearGraphProgressTimer();
          }
        }
      } catch (err) {
        console.error("Error polling graph progress", err);
        clearGraphProgressTimer();
      }
    }, 800);

    (async () => {
      try {
        const response = await api.get(
          `/graph_individual/${micro_id.micro_id}/${layers}/${incubation}/${inf_period_before}/${inf_period_after}/${contam_period}/${test_time_buffer}/${overlap_level}/?job_id=${jobId}`
        );

        if (graphRequestIdRef.current !== requestId) return;

        if (response.ok) {
          const parsedGraph = parseGraph(response.body);
          dispatch(
            patientActions.storeGraph(
              parsedGraph.nodes,
              parsedGraph.edges,
              desiredGraphKey
            )
          );
          dispatch(patientActions.graphProgressSet(100));
        } else {
          console.log("error");
        }
      } catch (err) {
        console.error("Error fetching graph", err);
      } finally {
        if (graphRequestIdRef.current === requestId) {
          clearGraphProgressTimer();
          dispatch(patientActions.graphLoadingEnd());
        }
      }
    })();

    return () => {
      graphRequestIdRef.current += 1;
      clearGraphProgressTimer();
      dispatch(patientActions.graphLoadingEnd());
    };
  }, [
    ptId,
    adm_start,
    graphIsFresh,
    desiredGraphKey,
    api,
    dispatch,
    micro_id.micro_id,
    layers,
    incubation,
    inf_period_before,
    inf_period_after,
    contam_period,
    test_time_buffer,
    overlap_level,
  ]);

  const mergedNameMap = useMemo(
    () => ({ ...treeNameMapSnp2, ...treeNameMap }),
    [treeNameMapSnp2, treeNameMap]
  );

  const survey_mode =
    survey.surveyHistory[activeCase?.micro_id]?.mode || 0;
  const geneticHighlightEnabled =
    (!debug_mode && survey_mode >= 3) ||
    (debug_mode && debugSurveyMode >= 3);

  const graphNodeIdSet = useMemo(
    () => new Set((nodes || []).map((n) => String(n.id))),
    [nodes]
  );

  const geneticMatchSeqIds = useMemo(() => {
    const seqIds = new Set();
    Object.entries(mergedNameMap || {}).forEach(([seqId, entry]) => {
      if (Array.isArray(entry)) {
        const [, microId] = entry;
        if (
          microId !== null &&
          microId !== undefined &&
          graphNodeIdSet.has(String(microId))
        ) {
          seqIds.add(String(seqId));
        }
      }
    });
    return Array.from(seqIds);
  }, [mergedNameMap, graphNodeIdSet]);

  const seqIdConstraintSet = useMemo(() => {
    if (!overlap_constraint) return null;
    const baseSeqIds =
      Array.isArray(treeConstraintSeqIds) && treeConstraintSeqIds.length > 0
        ? treeConstraintSeqIds
        : geneticMatchSeqIds;
    if (!baseSeqIds || baseSeqIds.length === 0) return null;
    return new Set(baseSeqIds.map((id) => String(id)));
  }, [overlap_constraint, treeConstraintSeqIds, geneticMatchSeqIds]);

  const seqIdTargetNodeIds = useMemo(() => {
    if (!seqIdConstraintSet) return [];
    return (nodes || [])
      .filter((node) => {
        const nodeSeqId =
          node && node.seq_id !== null && node.seq_id !== undefined
            ? String(node.seq_id)
            : null;
        return nodeSeqId !== null && seqIdConstraintSet.has(nodeSeqId);
      })
      .map((node) => String(node.id));
  }, [nodes, seqIdConstraintSet]);

  const seqIdPathNodeSet = useMemo(() => {
    if (!seqIdConstraintSet || seqIdTargetNodeIds.length === 0) return null;

    const adj = new Map();
    (edges || []).forEach(({ source, target }) => {
      const s = String(source);
      const t = String(target);
      if (!adj.has(s)) adj.set(s, []);
      if (!adj.has(t)) adj.set(t, []);
      adj.get(s).push(t);
      adj.get(t).push(s);
    });

    const shortestPath = (start, target) => {
      if (start === target) return [start];
      const queue = [start];
      const visited = new Set([start]);
      const parent = new Map();
      while (queue.length) {
        const current = queue.shift();
        const neighbours = adj.get(current) || [];
        for (const next of neighbours) {
          if (visited.has(next)) continue;
          visited.add(next);
          parent.set(next, current);
          if (next === target) {
            const path = [next];
            let p = current;
            while (p) {
              path.push(p);
              p = parent.get(p);
            }
            return path.reverse();
          }
          queue.push(next);
        }
      }
      return null;
    };

    const keep = new Set();
    const root = String(ptId);
    keep.add(root);
    seqIdTargetNodeIds.forEach((targetId) => {
      const target = String(targetId);
      const path = shortestPath(root, target);
      if (path) {
        path.forEach((nodeId) => keep.add(nodeId));
      } else {
        keep.add(target);
      }
    });

    return keep;
  }, [edges, ptId, seqIdConstraintSet, seqIdTargetNodeIds]);

  const constrain = useMemo(() => {
    if (!pruneForPhylos) return null;

    if (overlap_constraint) {
      if (Array.isArray(graphConstraints) && graphConstraints.length > 0) {
        return new Set([...graphConstraints.map((id) => String(id)), String(ptId)]);
      }

      if (Array.isArray(treeConstraintSeqIds) && treeConstraintSeqIds.length > 0) {
        if (seqIdPathNodeSet && seqIdPathNodeSet.size > 0) {
          return new Set(seqIdPathNodeSet);
        }
        const seqIdSet = new Set(treeConstraintSeqIds.map((id) => String(id)));
        const idSet = new Set([String(ptId)]);
        (nodes || []).forEach((node) => {
          const nodeSeqId =
            node && node.seq_id !== null && node.seq_id !== undefined
              ? String(node.seq_id)
              : null;
          if (nodeSeqId !== null && seqIdSet.has(nodeSeqId)) {
            idSet.add(String(node.id));
          }
        });
        return idSet;
      }

      return new Set([String(ptId)]);
    }

    return Array.isArray(graphConstraints)
      ? new Set([...graphConstraints.map((id) => String(id)), String(ptId)])
      : new Set([String(ptId)]);
  }, [
    pruneForPhylos,
    overlap_constraint,
    treeConstraintSeqIds,
    graphConstraints,
    nodes,
    ptId,
    seqIdPathNodeSet,
  ]);

  const pruneNodes = (nodeList, phylos, pathNodes, indexId) =>
    nodeList.filter((node) => {
      if (!phylos.has(String(node.id))) return false;
      if (!pathNodes || pathNodes.size === 0) return true;
      if (String(node.id) === String(indexId)) return true;
      return pathNodes.has(String(node.id));
    });

  const pruneEdges = (edgeList, nodeIdSet) =>
    edgeList.filter(
      (edge) =>
        nodeIdSet.has(String(edge.source)) &&
        nodeIdSet.has(String(edge.target))
    );

  const prunedNodes =
    constrain !== null
      ? pruneNodes(nodes, constrain, seqIdPathNodeSet, ptId)
      : nodes;
  const prunedNodeIdSet = new Set(
    (prunedNodes || []).map((node) => String(node.id))
  );
  const prunedEdges =
    constrain !== null ? pruneEdges(edges, prunedNodeIdSet) : edges;

  return (
    <>
      {ptId !== micro_id.micro_id ? (
        <Spinner animation="border" />
      ) : (
        <div className="graph-wrapper">
          <div className="graph-menu-row">
            <GraphMenu
              adm={adm_start === null}
              showGeneticToggle={geneticHighlightEnabled}
            />
          </div>
          <div className="graph_chart_container">
            <div className="graph_chart">
              {(graphLoading || (!graphIsFresh && adm_start !== null)) && (
                <div className="graph-loading-overlay">
                  <Spinner animation="border" role="status" size="sm" />
                  <span className="graph-loading-text">
                    Loading graph… {Math.round(graphProgress || 0)}%
                  </span>
                </div>
              )}
              {!graphIsFresh && adm_start !== null ? null : prunedNodes.length === 0 ? (
                <p className="mb-0">
                  There are no connections at this number of layers.
                </p>
              ) : (
                <GraphChart
                  micro_id={micro_id.micro_id}
                  nodes={prunedNodes}
                  edges={prunedEdges}
                  hoverToggled={graph.info}
                  geneticMatchSeqIds={geneticMatchSeqIds}
                  geneticHighlightEnabled={geneticHighlightEnabled}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Graph;
